// The whole situation on the order page, without opening WhatsApp.
//
// Owner, 2026-09-17: « a short summary at the top — what they asked, where it
// stands, what they are waiting on — the full WhatsApp conversation below,
// readable without leaving the page. I open one order and understand the whole
// situation. »
//
// ⚠️ THE SUMMARY IS FACTS, NOT A MODEL. Stage, who holds the ball, how long,
// their last words and ours, all read off the order and the chat by the server
// (`GET /pilotage/order-thread`). Opening an order costs nothing and can never
// invent a status.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pilotageApi, whatsappApi } from '@/lib/api';
import { conversationHref } from '@/lib/conversationLink';
import { useT } from '@/lib/i18n';

const clock = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export function OrderConversationCard({ orderKey, onOpenChat }: {
  orderKey: string;
  /** The order page's chat drawer (2026-10-07): open it in place instead of going to Conversations. */
  onOpenChat?: () => void;
}) {
  const { t } = useT();
  // Open by default: « I open one order and understand the whole situation ».
  // The thread scrolls inside its own box, so it never buries the workflow.
  const [open, setOpen] = useState(!onOpenChat);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['order-thread', orderKey],
    queryFn: () => pilotageApi.orderThread(orderKey),
    refetchInterval: 60_000,
  });
  // « Plainte résolue » (owner, 2026-10-15) — a complaint is closed by a person, never by the code.
  const qc = useQueryClient();
  const resolve = useMutation({
    mutationFn: (chatId: string) => whatsappApi.resolveComplaint(chatId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-thread', orderKey] });
      qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
  });

  if (isLoading) {
    return <div className="bg-white rounded-xl border border-gray-200 p-5 text-[12.5px] text-gray-400">{t('Chargement de la conversation…')}</div>;
  }
  if (isError || !data) return null;   // the rest of the page must never depend on this card

  const s = data.summary;
  const msgs = data.messages;
  // Answering, photos and the order card live in Conversations — one tap there
  // (owner, 2026-09-28).
  const chatHref = conversationHref(data.chatId);

  return (
    <div className="bg-white rounded-xl border border-gray-200 storiz-fade-in">
      <div className="p-5 space-y-3">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">{t('Où en est ce client')}</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">{t('Résumé factuel — aucune IA')}</p>
          </div>
          <div className="flex gap-1.5 flex-wrap items-center">
            {s.stuck && <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-700">{t('bloqué')}</span>}
            {s.complaint && <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-700">{t('mécontent')}</span>}
            {s.complaint && data.chatId && (
              <button type="button" disabled={resolve.isPending}
                onClick={() => { if (window.confirm(t('Marquer la plainte comme résolue ? L’agent reprend pour ce client (relances, offres, reprise après 15 min).'))) resolve.mutate(data.chatId!); }}
                className="inline-flex items-center min-h-[32px] px-2.5 py-1 rounded-lg border border-red-300 text-red-700 text-[12px] font-semibold hover:bg-red-50 disabled:opacity-50 cursor-pointer">
                ✓ {t('Plainte résolue')}
              </button>
            )}
            {s.paused && <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-600">{t('un humain a la main')}</span>}
            {onOpenChat ? (
              <button type="button" onClick={onOpenChat}
                className="inline-flex items-center gap-1.5 min-h-[36px] px-3 py-1.5 rounded-lg bg-adm-gold text-[#2B2116] dark:text-[#2B2116] text-[12.5px] font-semibold hover:brightness-95 cursor-pointer">
                💬 {t('Ouvrir le chat')}
              </button>
            ) : chatHref && (
              <Link
                to={chatHref}
                className="inline-flex items-center gap-1.5 min-h-[36px] px-3 py-1.5 rounded-lg bg-brand-500 text-white text-[12.5px] font-semibold hover:bg-brand-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 transition-colors duration-150 cursor-pointer"
              >
                💬 {t('Ouvrir la conversation')}
              </Link>
            )}
          </div>
        </header>

        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-3 text-[12.5px]">
          <div>
            <dt className="text-[10.5px] font-medium uppercase tracking-wide text-gray-400">{t('Étape')}</dt>
            <dd className="text-gray-900 font-medium">{s.stage} <span className={`tabular-nums font-normal ${s.stuck ? 'text-red-600' : 'text-gray-500'}`}>· {s.since}</span></dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-[10.5px] font-medium uppercase tracking-wide text-gray-400">{t('On attend')}</dt>
            <dd className="text-gray-900">{s.waitingOn}</dd>
          </div>
        </dl>

        {s.needsHuman && (
          <p className="text-[12px] text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
            {t('L’assistant a passé la main')} : {s.needsHuman}
          </p>
        )}
        {s.unansweredCount > 0 && (
          <p className="text-[12px] text-red-700 bg-red-50 rounded-lg px-3 py-2">
            {t('{n} message(s) du client sans réponse depuis {d}', { n: String(s.unansweredCount), d: s.unansweredSince ?? '—' })}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-[10.5px] font-medium uppercase tracking-wide text-gray-400 mb-1">{t('Ce qu’il a demandé')}</p>
            {s.theyAsked.length === 0
              ? <p className="text-[12px] text-gray-400">{data.chatId ? t('Aucun message écrit.') : t('Pas de conversation WhatsApp liée.')}</p>
              : <ul className="space-y-1">{s.theyAsked.map((a, i) => (
                <li key={i} className="text-[12.5px] text-gray-800 bg-gray-100 rounded-2xl px-3 py-1.5 whitespace-pre-wrap break-words">{a}</li>
              ))}</ul>}
          </div>
          <div>
            <p className="text-[10.5px] font-medium uppercase tracking-wide text-gray-400 mb-1">{t('Notre dernier message')}</p>
            {s.weSaid
              ? <p className="text-[12.5px] text-gray-800 bg-brand-50 rounded-2xl px-3 py-1.5 whitespace-pre-wrap break-words">{s.weSaid}</p>
              : <p className="text-[12px] text-gray-400">{t('Nous n’avons encore rien écrit.')}</p>}
          </div>
        </div>
      </div>

      {data.chatId && (
        <>
          <button
            type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
            className="w-full border-t border-gray-100 px-5 py-2.5 flex items-center justify-between text-[12px] font-semibold text-gray-700 hover:bg-gray-50/70 cursor-pointer transition-colors duration-150"
          >
            <span>{open ? t('Masquer la conversation') : t('Lire toute la conversation ({n} messages)', { n: String(msgs.length) })}</span>
            <span className={`text-gray-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}>▾</span>
          </button>
          {open && (
            <div className="border-t border-gray-100 px-5 py-4 space-y-2 max-h-[520px] overflow-y-auto storiz-fade-in">
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[84%] px-3 py-2 rounded-2xl text-[12.5px] leading-snug whitespace-pre-wrap break-words ${
                    m.direction === 'out' ? 'bg-brand-50 text-gray-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {m.body || <span className="italic text-gray-500">{t('(pièce jointe)')}</span>}
                    <span className="block text-[10px] text-gray-400 mt-0.5 tabular-nums">{clock(m.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
